import React from 'react';

import { Backdrop, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import Grow from '@mui/material/Grow';
import Popper, { PopperProps } from '@mui/material/Popper';
import { useTheme } from '@mui/material/styles';

import { getScroller } from '../ScheduleCalendar.helpers';

export type AnimatedPopoverProps = {
  anchor?: HTMLElement;
  open: boolean;
  onClose: () => void;
  placement?: PopperProps['placement'];
  modifiers?: PopperProps['modifiers'];
  autoScroll?: boolean;
};


const popperModifiers = [
  {
    name: 'preventOverflow',
    options: {
      padding: 8,
      rootBoundary: 'viewport',
    },
  },
  {
    name: 'offset',
    options: {
      offset: [ 8, 8 ],
    },
  },
  {
    name: 'computeStyles',
    options: {
      adaptive: false,
    },
  }
];

type PopperReferenceObject = {
  clientWidth: number;
  clientHeight: number;
  getBoundingClientRect(): DOMRect;
};

type DOMRectJSON = Omit<DOMRect, 'toJSON'>;

const useStyles = makeStyles()({
  popper: {
    position: 'fixed',
  }
});

export const AnimatedPopover: React.FC<AnimatedPopoverProps> = ({ children, anchor, open, onClose, autoScroll, placement = 'left', modifiers = [] }) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const closableRef = React.useRef(false);
  const [ animated, setAnimated ] = React.useState(false);
  const transitionDuration = animated ? '350ms' : undefined;

  const handleClose = React.useCallback(() => {
    setAnimated(false);
    onClose?.();
  }, [ onClose ]);

  React.useEffect(() => {
    const handleEscPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscPress);

    return () => document.removeEventListener('keydown', handleEscPress);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const backdropStyles = React.useMemo(() => ({
    zIndex: theme.zIndex.modal
  }), [ theme.zIndex.modal ]);

  const popperStyles = React.useMemo<React.CSSProperties>(() => ({
    zIndex: theme.zIndex.modal,
    left: undefined,
    top: undefined,
    transitionDuration,
  }), [ theme.zIndex.modal, transitionDuration ]);

  const handleBackdropClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!closableRef.current) return;
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  React.useEffect(() => {
    if (autoScroll === false) return;
    const [ scroller, scrollerBody ] = getScroller();

    if (!scroller || !anchor) {
      return;
    }

    const anchorRect = anchor.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const scrollerBodyRect = scrollerBody?.getBoundingClientRect();

    const eventRelativeTop = anchorRect.top - (scrollerBodyRect?.top ?? 0);
    const eventRelativeBottom = anchorRect.bottom - scrollerRect.bottom;
    const isTopOutsideOfTheScreenTop = eventRelativeTop < scroller.scrollTop;
    const isTopOutsideOfTheScreenBottom = eventRelativeBottom > 0;

    let realScrollTop: number | undefined = undefined;

    if (isTopOutsideOfTheScreenTop) {
      realScrollTop = Math.max(eventRelativeTop - 120, 0);
    } else if (isTopOutsideOfTheScreenBottom) {
      realScrollTop = scroller.scrollTop + eventRelativeBottom + 120;
    }

    if (realScrollTop !== undefined) {
      scroller.scrollTo({
        top: realScrollTop,
        behavior: 'smooth',
      });
    }
  }, [ anchor, autoScroll ]);

  const prevRectRef = React.useRef<DOMRectJSON>();
  const fakeAnchor = React.useMemo(() => {
    if (!anchor) {
      if (prevRectRef.current) {
        const rect = prevRectRef.current;

        return {
          clientWidth: rect.width,
          clientHeight: rect.height,
          getBoundingClientRect: () => rect as DOMRect,
        };
      }

      return anchor;
    }
    const [ scroller, scrollerBody ] = getScroller();
    const scrollerRect = scroller?.getBoundingClientRect();

    const getRect = (): DOMRectJSON => {
      const _anchorRect = anchor.getBoundingClientRect().toJSON() as DOMRectJSON;
      const isAnchor = _anchorRect.width && _anchorRect.height && _anchorRect.x && _anchorRect.y;
      const anchorRect = isAnchor ? _anchorRect : (prevRectRef.current ?? _anchorRect);

      prevRectRef.current = anchorRect;

      const scrollerBodyRect = scrollerBody?.getBoundingClientRect();

      const relativeTop = (scrollerBodyRect?.top ?? 0) + (scroller?.scrollTop ?? 0);
      const relativeBottom = scrollerRect?.bottom;

      const scrollUp = anchorRect.top - relativeTop < 0;
      const scrollDown = (relativeBottom ?? 0) - anchorRect.bottom < 0;

      if (scrollUp) {
        const space = Math.min(anchorRect.top - (scrollerBodyRect?.top ?? 0), 120);
        const realTop = relativeTop + space;

        return {
          ...anchorRect,
          top: realTop,
          bottom: realTop + anchorRect.height,
        };
      } else if (scrollDown) {
        const space = Math.min((scrollerBodyRect?.bottom ?? 0) - anchorRect.bottom, 120);
        const realBottom = (relativeBottom ?? 0) - space;

        return {
          ...anchorRect,
          top: realBottom - anchorRect.height,
          bottom: realBottom,
        };
      }

      return anchorRect;
    };

    const rect = getRect();

    return {
      clientWidth: rect.width,
      clientHeight: rect.height,
      getBoundingClientRect: getRect,
    } as PopperReferenceObject;
  }, [ anchor ]);

  return (
    <>
      <Backdrop open={open} style={backdropStyles} invisible onClick={handleBackdropClick} />
      <Popper
        keepMounted
        open={open}
        anchorEl={fakeAnchor}
        modifiers={[ ...popperModifiers, ...modifiers ]}
        popperOptions={{
          strategy: 'fixed',
        }}
        placement={placement}
        transition
        style={popperStyles}
        className={classes.popper}
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            timeout={150}
            onEntered={() => {
              setAnimated(true);
              setTimeout(() => {
                closableRef.current = true;
              }, 100);
            }}
            onExit={() => {
              setAnimated(false);
              closableRef.current = false;
            }}
          >
            <Paper elevation={20}>
              {children}
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};
